import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  Store,
  Snowflake,
  Users,
  Fingerprint,
  Delete,
  Loader2,
  Eye,
  EyeOff,
  ArrowLeft,
  KeyRound,
  AlertCircle,
  Check,
} from 'lucide-react';
import logo from '@/assets/images/logo.png';
import { estResponsableBoutique, estResponsableCF } from '@/utils/roleLabels';
import { ClavierTactile } from '@/components/shared/ClavierTactile';
import {
  loginTactileService,
  type LieuConnexion,
  type RoleSansLieu,
  type PersonneConnexion,
} from '@/services/loginTactileService';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';

/**
 * L'ecran de connexion tactile.
 *
 * Trois pas, toujours dans le meme ordre : ou (boutique, chambre froide, ou
 * un role sans lieu comme livreur/comptabilite), qui, puis comment. Chaque
 * pas se ferme par un grand bouton, pense pour un doigt sur une tablette au
 * comptoir plutot que pour une souris.
 *
 * Le mot de passe classique reste toujours a une touche : si ce nouvel
 * ecran a un souci sur un poste donne, personne ne reste bloque dehors le
 * temps d'un correctif.
 */

type Famille = 'boutiques' | 'chambres-froides' | 'autres-roles';
type Etape = 'lieu' | 'role' | 'personne' | 'auth' | 'nouveau-pin';

const getDashboardRoute = (roles: any[]) => {
  const roleNames = roles.map((role: any) => {
    if (typeof role === 'string') return role;
    if (role && typeof role === 'object') return role.name || role;
    return String(role);
  });

  if (roleNames.includes('dg') || roleNames.includes('director_general') || roleNames.includes('directeur_general')) {
    return '/dg/dashboard';
  }
  if (roleNames.includes('accountant') || roleNames.includes('comptable') || roleNames.includes('chief_accountant')) {
    return '/accountant/dashboard';
  }
  if (roleNames.includes('driver') || roleNames.includes('livreur') || roleNames.includes('delivery_driver')) {
    // « Ma journee » d'abord : sa journee preparee par les commerciaux,
    // dans l'ordre (consigne DG, 20/10) — "Mes Courses" reste accessible
    // depuis la, course par course.
    return '/driver/journee';
  }
  if (roleNames.includes('shop_seller') || roleNames.includes('vendeur') || roleNames.includes('seller')) {
    return '/seller/pos';
  }
  if (roleNames.includes('farm_agent') || roleNames.includes('agent_ferme') || roleNames.includes('farm_manager') || roleNames.includes('responsable_ferme')) {
    return '/farm/dashboard';
  }
  if (roleNames.some(estResponsableBoutique)) {
    return '/store/dashboard';
  }
  if (roleNames.some(estResponsableCF)) {
    return '/cold-storage/dashboard';
  }
  return '/admin/dashboard';
};

const initiales = (nom: string) =>
  nom.trim().split(/\s+/).slice(0, 2).map((m) => m[0]?.toUpperCase() || '').join('') || '?';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { applyLoginResponse, isAuthenticated, loading } = useAuth();

  const [famille, setFamille] = useState<Famille>('boutiques');
  const [etape, setEtape] = useState<Etape>('lieu');

  const [lieux, setLieux] = useState<LieuConnexion[]>([]);
  const [roles, setRoles] = useState<RoleSansLieu[]>([]);
  const [chargementLieux, setChargementLieux] = useState(true);

  const [lieuChoisi, setLieuChoisi] = useState<LieuConnexion | null>(null);
  const [roleChoisi, setRoleChoisi] = useState<RoleSansLieu | null>(null);
  const [personnes, setPersonnes] = useState<PersonneConnexion[]>([]);
  const [chargementPersonnes, setChargementPersonnes] = useState(false);
  const [personneChoisie, setPersonneChoisie] = useState<PersonneConnexion | null>(null);

  const [pin, setPin] = useState('');
  const [montrerMotDePasse, setMontrerMotDePasse] = useState(false);
  const [motDePasse, setMotDePasse] = useState('');
  const [voirMotDePasse, setVoirMotDePasse] = useState(false);
  const [empreinteEnCours, setEmpreinteEnCours] = useState(false);
  const [empreinteEchouee, setEmpreinteEchouee] = useState(false);
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);

  const [nouveauPin, setNouveauPin] = useState('');
  const [confirmationPin, setConfirmationPin] = useState('');
  const [etapeNouveauPin, setEtapeNouveauPin] = useState<'saisie' | 'confirmation'>('saisie');

  const tentativeEmpreinteFaite = useRef<string | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      const cachedUser = localStorage.getItem('user_data');
      if (cachedUser) {
        try {
          const userData = JSON.parse(cachedUser);
          navigate(getDashboardRoute(userData?.roles || []), { replace: true });
          return;
        } catch {
          /* on retombe sur le tableau de bord par defaut ci-dessous */
        }
      }
      navigate('/admin/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const chargerLieux = useCallback(async (f: Famille) => {
    setChargementLieux(true);
    setErreur('');
    try {
      if (f === 'boutiques') {
        setLieux(await loginTactileService.getShops());
      } else if (f === 'chambres-froides') {
        setLieux(await loginTactileService.getColdStorages());
      } else {
        setRoles(await loginTactileService.getRoles());
      }
    } catch {
      setErreur('Impossible de charger la liste — vérifiez la connexion');
    } finally {
      setChargementLieux(false);
    }
  }, []);

  /**
   * Les raccourcis "Comptabilité"/"Chauffeur" (consigne DG, 19/10) changent
   * la famille ET sautent directement a l'etape personne, sans repasser par
   * la grille "Autres rôles" — cette reference empeche cet effet d'ecraser
   * ce choix au rendu suivant.
   */
  const sauterResetFamille = useRef(false);
  useEffect(() => {
    if (sauterResetFamille.current) {
      sauterResetFamille.current = false;
      return;
    }
    setEtape(famille === 'autres-roles' ? 'role' : 'lieu');
    setLieuChoisi(null);
    setRoleChoisi(null);
    setPersonneChoisie(null);
    chargerLieux(famille);
  }, [famille, chargerLieux]);

  const choisirLieu = async (lieu: LieuConnexion) => {
    setLieuChoisi(lieu);
    setEtape('personne');
    setChargementPersonnes(true);
    setErreur('');
    try {
      const liste = famille === 'boutiques'
        ? await loginTactileService.getPeopleForShop(lieu.id)
        : await loginTactileService.getPeopleForColdStorage(lieu.id);
      setPersonnes(liste);
    } catch {
      setErreur('Impossible de charger les personnes de ce lieu');
    } finally {
      setChargementPersonnes(false);
    }
  };

  const choisirRole = async (r: RoleSansLieu) => {
    setRoleChoisi(r);
    setEtape('personne');
    setChargementPersonnes(true);
    setErreur('');
    try {
      setPersonnes(await loginTactileService.getPeopleForRole(r.code));
    } catch {
      setErreur('Impossible de charger les personnes');
    } finally {
      setChargementPersonnes(false);
    }
  };

  /**
   * "Comptabilité" couvre deux roles distincts (accountant, chief_accountant,
   * meme libelle cote utilisateur) — un raccourci direct depuis le bas de
   * l'ecran boutiques, sans passer par la grille "Autres rôles" (consigne
   * DG, 19/10).
   */
  const choisirComptabilite = async () => {
    sauterResetFamille.current = true;
    setFamille('autres-roles');
    setRoleChoisi({ code: 'accountant', label: 'Comptabilité' });
    setEtape('personne');
    setChargementPersonnes(true);
    setErreur('');
    try {
      const [a, b] = await Promise.all([
        loginTactileService.getPeopleForRole('accountant'),
        loginTactileService.getPeopleForRole('chief_accountant'),
      ]);
      setPersonnes([...a, ...b]);
    } catch {
      setErreur('Impossible de charger les personnes');
    } finally {
      setChargementPersonnes(false);
    }
  };

  const choisirChauffeur = async () => {
    sauterResetFamille.current = true;
    setFamille('autres-roles');
    await choisirRole({ code: 'delivery_driver', label: 'Chauffeur' });
  };

  const choisirCommercial = async () => {
    sauterResetFamille.current = true;
    setFamille('autres-roles');
    await choisirRole({ code: 'commercial', label: 'Commercial' });
  };

  const retourALaListe = () => {
    setEtape(famille === 'autres-roles' ? 'role' : 'lieu');
    setPersonneChoisie(null);
    setPin('');
    setErreur('');
    setMontrerMotDePasse(false);
    setEmpreinteEchouee(false);
  };

  const retourAuxPersonnes = () => {
    setEtape('personne');
    setPersonneChoisie(null);
    setPin('');
    setErreur('');
    setMontrerMotDePasse(false);
    setEmpreinteEchouee(false);
  };

  // ── Choisir une personne : tente l'empreinte d'abord si disponible, sinon le PIN directement. ──
  const choisirPersonne = async (p: PersonneConnexion) => {
    setPersonneChoisie(p);
    setEtape('auth');
    setPin('');
    setErreur('');
    setMontrerMotDePasse(!p.hasPin);
    setEmpreinteEchouee(false);
    tentativeEmpreinteFaite.current = null;

    if (p.hasWebAuthn && browserSupportsWebAuthn() && tentativeEmpreinteFaite.current !== p.userId) {
      tentativeEmpreinteFaite.current = p.userId;
      setEmpreinteEnCours(true);
      const reponse = await loginTactileService.tenterConnexionEmpreinte(p.userId);
      setEmpreinteEnCours(false);
      if (reponse) {
        await terminerConnexion(reponse);
      } else {
        setEmpreinteEchouee(true);
      }
    }
  };

  const terminerConnexion = async (reponse: any) => {
    const resultat = applyLoginResponse(reponse);
    if (resultat?.data?.requiresTwoFactor) {
      // La double authentification (compte admin/DG) ne tient pas dans le
      // pave tactile : ces comptes se connectent par la voie classique, ou
      // ce champ existe deja.
      navigate('/login/classique', { replace: true });
      return;
    }
    const user = resultat?.data?.user;
    if (user?.pin_must_change) {
      setEtape('nouveau-pin');
      setEtapeNouveauPin('saisie');
      setNouveauPin('');
      setConfirmationPin('');
      return;
    }
    navigate(getDashboardRoute(user?.roles || []), { replace: true });
  };

  const validerPin = async () => {
    if (!personneChoisie || pin.length < 4) return;
    setEnvoi(true);
    setErreur('');
    try {
      const reponse = await loginTactileService.loginByPin(personneChoisie.userId, pin);
      await terminerConnexion(reponse);
    } catch (e: any) {
      setErreur(e?.message || 'PIN incorrect');
      setPin('');
    } finally {
      setEnvoi(false);
    }
  };

  // Valide des que le PIN atteint la longueur attendue — pas de bouton a chercher au comptoir.
  useEffect(() => {
    if (etape === 'auth' && !montrerMotDePasse && pin.length === 4) {
      validerPin();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin]);

  const validerMotDePasse = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!personneChoisie || !motDePasse) return;
    setEnvoi(true);
    setErreur('');
    try {
      const reponse = await loginTactileService.loginByIdPassword(personneChoisie.userId, motDePasse);
      await terminerConnexion(reponse);
    } catch (e: any) {
      setErreur(e?.message || 'Mot de passe incorrect');
    } finally {
      setEnvoi(false);
    }
  };

  const validerNouveauPin = async () => {
    if (etapeNouveauPin === 'saisie') {
      if (nouveauPin.length < 4) return;
      setEtapeNouveauPin('confirmation');
      return;
    }
    if (confirmationPin !== nouveauPin) {
      setErreur('Les deux PIN ne correspondent pas');
      setConfirmationPin('');
      return;
    }
    setEnvoi(true);
    setErreur('');
    try {
      await loginTactileService.changePin(nouveauPin);
      const cachedUser = localStorage.getItem('user_data');
      const roles = cachedUser ? JSON.parse(cachedUser)?.roles || [] : [];
      navigate(getDashboardRoute(roles), { replace: true });
    } catch (e: any) {
      setErreur(e?.message || "Le PIN n'a pas pu être enregistré");
    } finally {
      setEnvoi(false);
    }
  };

  const appuyerChiffre = (chiffre: string, cible: 'pin' | 'nouveau-pin' | 'confirmation-pin') => {
    if (cible === 'pin') setPin((v) => (v.length < 6 ? v + chiffre : v));
    if (cible === 'nouveau-pin') setNouveauPin((v) => (v.length < 6 ? v + chiffre : v));
    if (cible === 'confirmation-pin') setConfirmationPin((v) => (v.length < 6 ? v + chiffre : v));
  };
  const effacerChiffre = (cible: 'pin' | 'nouveau-pin' | 'confirmation-pin') => {
    if (cible === 'pin') setPin((v) => v.slice(0, -1));
    if (cible === 'nouveau-pin') setNouveauPin((v) => v.slice(0, -1));
    if (cible === 'confirmation-pin') setConfirmationPin((v) => v.slice(0, -1));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-600 flex flex-col items-center justify-center">
        <div className="bg-white rounded-3xl p-8 shadow-2xl mb-8">
          <img src={logo} alt="Volaille d'Or" className="h-24 w-24 object-contain" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-4">Volaille d'Or</h2>
        <div className="flex items-center gap-3 text-primary-100">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Chargement...</span>
        </div>
      </div>
    );
  }

  const PaveNumerique = ({
    valeur,
    cible,
    disabled,
  }: {
    valeur: string;
    cible: 'pin' | 'nouveau-pin' | 'confirmation-pin';
    disabled?: boolean;
  }) => (
    <div className="w-full max-w-xs mx-auto">
      <div className="flex items-center justify-center gap-3 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <span
            key={i}
            className={`w-4 h-4 rounded-full border-2 ${
              i < valeur.length ? 'bg-primary-600 border-primary-600' : 'border-neutral-300'
            }`}
          />
        ))}
        {valeur.length > 4 && (
          <span className="text-xs text-neutral-400 ml-1">+{valeur.length - 4}</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((c) => (
          <button
            key={c}
            type="button"
            disabled={disabled}
            onClick={() => appuyerChiffre(c, cible)}
            className="h-20 w-20 min-h-[80px] min-w-[80px] mx-auto rounded-2xl bg-white border-2 border-neutral-200 text-2xl font-bold text-neutral-800 hover:border-primary-400 active:bg-primary-50 transition-all disabled:opacity-40"
          >
            {c}
          </button>
        ))}
        <div />
        <button
          type="button"
          disabled={disabled}
          onClick={() => appuyerChiffre('0', cible)}
          className="h-20 w-20 min-h-[80px] min-w-[80px] mx-auto rounded-2xl bg-white border-2 border-neutral-200 text-2xl font-bold text-neutral-800 hover:border-primary-400 active:bg-primary-50 transition-all disabled:opacity-40"
        >
          0
        </button>
        <button
          type="button"
          disabled={disabled || valeur.length === 0}
          onClick={() => effacerChiffre(cible)}
          className="h-20 w-20 min-h-[80px] min-w-[80px] mx-auto rounded-2xl bg-neutral-100 border-2 border-neutral-200 flex items-center justify-center text-neutral-600 hover:border-neutral-300 active:bg-neutral-200 transition-all disabled:opacity-40"
        >
          <Delete className="w-6 h-6" />
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center py-8 px-4">
        <div className="w-full max-w-4xl">
          <div className="flex flex-col items-center mb-6">
            <div className="bg-white rounded-2xl p-3 shadow-card border border-neutral-200 mb-3">
              <img src={logo} alt="Volaille d'Or" className="h-14 w-14 object-contain" />
            </div>
            <h1 className="text-xl font-bold text-neutral-900">Volaille d'Or</h1>
          </div>

          {/* Fil d'ariane */}
          {etape !== 'lieu' && etape !== 'role' && (
            <button
              onClick={etape === 'personne' ? retourALaListe : retourAuxPersonnes}
              className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 mb-4 font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </button>
          )}

          <div className="bg-white rounded-3xl shadow-card border border-neutral-200 p-6 sm:p-10">
            {/* ── ETAPE : choisir la famille (boutique / CF / autre) ── */}
            {(etape === 'lieu' || etape === 'role') && (
              <>
                {/*
                  Cet ecran tactile n'est plus que pour les boutiques : chambre
                  froide et les autres comptes (admin, DG, comptable, livreur...)
                  passent desormais par la connexion classique (email + mot de
                  passe), deja presente en bas d'ecran. Le selecteur de famille
                  n'a donc plus lieu d'etre.
                */}
                <h2 className="text-center text-lg font-semibold text-neutral-800 mb-6">
                  {famille === 'boutiques'
                    ? 'De quelle boutique êtes-vous ?'
                    : famille === 'chambres-froides'
                      ? 'De quelle chambre froide êtes-vous ?'
                      : 'Choisissez votre rôle'}
                </h2>

                {erreur && (
                  <div className="flex items-center gap-2 p-3 mb-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{erreur}</span>
                  </div>
                )}

                {famille !== 'boutiques' && (
                  <button
                    onClick={() => setFamille('boutiques')}
                    className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 mb-4 font-medium"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour aux boutiques
                  </button>
                )}

                {chargementLieux ? (
                  <div className="flex justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-neutral-300" />
                  </div>
                ) : famille === 'autres-roles' ? (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {roles.map((r) => (
                      <button
                        key={r.code}
                        onClick={() => choisirRole(r)}
                        className="flex flex-col items-center gap-2 p-5 rounded-2xl border-2 border-neutral-200 hover:border-primary-400 hover:bg-primary-50 transition-all min-h-[110px]"
                      >
                        <Users className="w-8 h-8 text-primary-600" />
                        <span className="text-sm font-semibold text-neutral-800 text-center">{r.label}</span>
                      </button>
                    ))}
                  </div>
                ) : lieux.length === 0 ? (
                  <p className="text-center text-neutral-400 py-16">Aucun lieu disponible.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {lieux.map((l) => (
                      <button
                        key={l.id}
                        onClick={() => choisirLieu(l)}
                        className="flex flex-col items-center gap-3 p-5 rounded-2xl border-2 border-neutral-200 hover:border-primary-400 hover:bg-primary-50 transition-all min-h-[140px]"
                      >
                        {l.logoUrl ? (
                          <img src={l.logoUrl} alt={l.name} className="w-16 h-16 object-contain rounded-xl" />
                        ) : (
                          <div className="w-16 h-16 rounded-xl bg-primary-100 flex items-center justify-center">
                            {famille === 'boutiques' ? (
                              <Store className="w-8 h-8 text-primary-600" />
                            ) : (
                              <Snowflake className="w-8 h-8 text-primary-600" />
                            )}
                          </div>
                        )}
                        <span className="text-base font-bold text-neutral-900 text-center">{l.name}</span>
                      </button>
                    ))}
                  </div>
                )}

                {/* Comptabilite / Chauffeur / Chambre froide — connexion tactile
                    par PIN, a cote des boutiques (consigne DG, 19/10). */}
                {famille === 'boutiques' && !chargementLieux && (
                  <div className="mt-8 pt-6 border-t border-neutral-100">
                    <p className="text-center text-xs text-neutral-400 mb-4">
                      Ou connectez-vous en tant que
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <button
                        onClick={choisirCommercial}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-neutral-200 hover:border-primary-400 hover:bg-primary-50 transition-all"
                      >
                        <Users className="w-6 h-6 text-primary-600" />
                        <span className="text-xs font-semibold text-neutral-800 text-center">Commercial</span>
                      </button>
                      <button
                        onClick={choisirComptabilite}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-neutral-200 hover:border-primary-400 hover:bg-primary-50 transition-all"
                      >
                        <Users className="w-6 h-6 text-primary-600" />
                        <span className="text-xs font-semibold text-neutral-800 text-center">Comptabilité</span>
                      </button>
                      <button
                        onClick={choisirChauffeur}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-neutral-200 hover:border-primary-400 hover:bg-primary-50 transition-all"
                      >
                        <Users className="w-6 h-6 text-primary-600" />
                        <span className="text-xs font-semibold text-neutral-800 text-center">Chauffeur</span>
                      </button>
                      <button
                        onClick={() => setFamille('chambres-froides')}
                        className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-neutral-200 hover:border-primary-400 hover:bg-primary-50 transition-all"
                      >
                        <Snowflake className="w-6 h-6 text-primary-600" />
                        <span className="text-xs font-semibold text-neutral-800 text-center">Chambre froide</span>
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── ETAPE : choisir la personne ── */}
            {etape === 'personne' && (
              <>
                <h2 className="text-center text-lg font-semibold text-neutral-800 mb-6">
                  {lieuChoisi ? lieuChoisi.name : roleChoisi?.label} — qui êtes-vous ?
                </h2>

                {erreur && (
                  <div className="flex items-center gap-2 p-3 mb-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{erreur}</span>
                  </div>
                )}

                {chargementPersonnes ? (
                  <div className="flex justify-center py-16">
                    <Loader2 className="w-8 h-8 animate-spin text-neutral-300" />
                  </div>
                ) : personnes.length === 0 ? (
                  <p className="text-center text-neutral-400 py-16">Aucun compte trouvé ici.</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {personnes.map((p) => (
                      <button
                        key={p.userId}
                        onClick={() => choisirPersonne(p)}
                        className="flex flex-col items-center gap-2 p-4 rounded-2xl border-2 border-neutral-200 hover:border-primary-400 hover:bg-primary-50 transition-all min-h-[140px]"
                      >
                        {p.photo ? (
                          <img src={p.photo} alt={p.name} className="w-16 h-16 rounded-full object-cover" />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-primary-600 flex items-center justify-center text-white text-lg font-bold">
                            {initiales(p.name)}
                          </div>
                        )}
                        <span className="text-sm font-bold text-neutral-900 text-center leading-tight">{p.name}</span>
                        <span className="text-[11px] text-neutral-400">{p.role}</span>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* ── ETAPE : authentification ── */}
            {etape === 'auth' && personneChoisie && (
              <div className="flex flex-col items-center">
                <div className="mb-6 flex flex-col items-center">
                  {personneChoisie.photo ? (
                    <img src={personneChoisie.photo} alt="" className="w-20 h-20 rounded-full object-cover mb-3" />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-primary-600 flex items-center justify-center text-white text-2xl font-bold mb-3">
                      {initiales(personneChoisie.name)}
                    </div>
                  )}
                  <h2 className="text-lg font-bold text-neutral-900">{personneChoisie.name}</h2>
                  <p className="text-sm text-neutral-400">{personneChoisie.role}</p>
                </div>

                {empreinteEnCours ? (
                  <div className="flex flex-col items-center gap-4 py-8">
                    <div className="w-20 h-20 rounded-full bg-primary-50 flex items-center justify-center animate-pulse">
                      <Fingerprint className="w-10 h-10 text-primary-600" />
                    </div>
                    <p className="text-sm font-medium text-neutral-600">Posez votre doigt sur le capteur…</p>
                  </div>
                ) : montrerMotDePasse ? (
                  <form onSubmit={validerMotDePasse} className="w-full max-w-xs space-y-4">
                    {!personneChoisie.hasPin && (
                      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-center">
                        Aucun PIN configuré pour ce compte — connectez-vous avec le mot de passe.
                      </p>
                    )}
                    <div className="relative">
                      <input
                        type={voirMotDePasse ? 'text' : 'password'}
                        value={motDePasse}
                        onChange={(e) => setMotDePasse(e.target.value)}
                        placeholder="Mot de passe"
                        className="form-input pr-12 text-center text-lg"
                        autoFocus
                        autoComplete="current-password"
                        disabled={envoi}
                      />
                      <button
                        type="button"
                        onClick={() => setVoirMotDePasse((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400"
                        tabIndex={-1}
                      >
                        {voirMotDePasse ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {erreur && (
                      <div className="flex items-center gap-2 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{erreur}</span>
                      </div>
                    )}
                    <button type="submit" disabled={envoi || !motDePasse} className="btn-primary btn-lg w-full">
                      {envoi ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Se connecter'}
                    </button>
                    {personneChoisie.hasPin && (
                      <button
                        type="button"
                        onClick={() => { setMontrerMotDePasse(false); setErreur(''); }}
                        className="w-full text-sm text-neutral-500 hover:text-neutral-700 font-medium"
                      >
                        ← Utiliser le PIN
                      </button>
                    )}
                  </form>
                ) : (
                  <>
                    {empreinteEchouee && (
                      <p className="text-xs text-neutral-400 mb-4 text-center">
                        Empreinte non reconnue — utilisez votre PIN.
                      </p>
                    )}
                    {erreur && (
                      <div className="flex items-center gap-2 p-3 mb-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm max-w-xs">
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                        <span>{erreur}</span>
                      </div>
                    )}
                    <p className="text-sm text-neutral-500 mb-4">Entrez votre PIN</p>
                    <PaveNumerique valeur={pin} cible="pin" disabled={envoi} />
                    {envoi && <Loader2 className="w-5 h-5 animate-spin text-primary-600 mt-4" />}
                    <button
                      type="button"
                      onClick={() => { setMontrerMotDePasse(true); setErreur(''); }}
                      className="mt-6 flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 font-medium"
                    >
                      <KeyRound className="w-4 h-4" />
                      Utiliser le mot de passe
                    </button>
                  </>
                )}
              </div>
            )}

            {/* ── ETAPE : premiere connexion, choisir un nouveau PIN ── */}
            {etape === 'nouveau-pin' && (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full bg-primary-50 flex items-center justify-center mb-4">
                  <KeyRound className="w-8 h-8 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-neutral-900 mb-1">
                  {etapeNouveauPin === 'saisie' ? 'Choisissez votre PIN' : 'Confirmez votre PIN'}
                </h2>
                <p className="text-sm text-neutral-500 mb-6 text-center max-w-xs">
                  {etapeNouveauPin === 'saisie'
                    ? 'Première connexion : remplacez le PIN qui vous a été donné par un PIN à vous, 4 chiffres.'
                    : 'Retapez le même PIN pour confirmer.'}
                </p>
                {erreur && (
                  <div className="flex items-center gap-2 p-3 mb-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span>{erreur}</span>
                  </div>
                )}
                {etapeNouveauPin === 'saisie' ? (
                  <PaveNumerique valeur={nouveauPin} cible="nouveau-pin" disabled={envoi} />
                ) : (
                  <PaveNumerique valeur={confirmationPin} cible="confirmation-pin" disabled={envoi} />
                )}
                <button
                  type="button"
                  disabled={
                    envoi ||
                    (etapeNouveauPin === 'saisie' ? nouveauPin.length < 4 : confirmationPin.length < 4)
                  }
                  onClick={validerNouveauPin}
                  className="btn-primary btn-lg w-full max-w-xs mt-6 flex items-center justify-center gap-2"
                >
                  {envoi ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
                  {etapeNouveauPin === 'saisie' ? 'Continuer' : 'Confirmer'}
                </button>
              </div>
            )}
          </div>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/login/classique')}
              className="text-sm text-neutral-400 hover:text-neutral-600 font-medium transition-colors"
            >
              Connexion classique (email + mot de passe)
            </button>
          </div>
        </div>
      </div>

      {/* Pour le champ mot de passe (Option C) : ne recouvre jamais l'ecran. */}
      <ClavierTactile />
    </>
  );
};
